'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Sparkles, Plus, Users, FileText, X, HardHat, Search, Star } from 'lucide-react';

interface WelcomeCardProps {
  userType: 'contractor' | 'worker';
  userName?: string;
  hasPostedJob?: boolean;
  hasApplied?: boolean;
}

export function WelcomeCard({ userType, userName, hasPostedJob, hasApplied }: WelcomeCardProps) {
  const [dismissed, setDismissed] = useState(false);
  const storageKey = `rateright-welcome-dismissed-${userType}`;

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setDismissed(localStorage.getItem(storageKey) === 'true');
    }
  }, [storageKey]);

  const dismiss = () => {
    setDismissed(true);
    localStorage.setItem(storageKey, 'true');
  };

  if (dismissed) return null;

  if (userType === 'contractor') {
    return (
      <Card className="border-primary/30 bg-gradient-to-br from-primary/5 to-primary/10 relative overflow-hidden">
        <button
          onClick={dismiss}
          className="absolute top-3 right-3 text-muted-foreground hover:text-foreground p-1 z-10"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-xl">
            <Sparkles className="h-6 w-6 text-primary" />
            Welcome{userName ? `, ${userName}` : ''}! 🎉
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-muted-foreground">
            You&apos;re all set. Here&apos;s how to find your first worker:
          </p>
          <div className="space-y-3">
            <Link href="/contractor/post-job" className="flex items-center gap-3 rounded-xl bg-background border p-3 hover:bg-muted/50 transition-colors active:scale-[0.98]">
              <div className="flex items-center justify-center h-8 w-8 rounded-full bg-primary/10 shrink-0">
                <Plus className="h-4 w-4 text-primary" />
              </div>
              <div>
                <p className="font-medium text-sm">Post a job</p>
                <p className="text-xs text-muted-foreground">AI writes it — you just describe what you need</p>
              </div>
            </Link>
            <div className="flex items-center gap-3 rounded-xl bg-background/50 border border-dashed p-3 opacity-70">
              <div className="flex items-center justify-center h-8 w-8 rounded-full bg-muted shrink-0">
                <Users className="h-4 w-4 text-muted-foreground" />
              </div>
              <div>
                <p className="font-medium text-sm">Review matches</p>
                <p className="text-xs text-muted-foreground">Workers apply → you pick the best fit</p>
              </div>
            </div>
            <div className="flex items-center gap-3 rounded-xl bg-background/50 border border-dashed p-3 opacity-70">
              <div className="flex items-center justify-center h-8 w-8 rounded-full bg-muted shrink-0">
                <Star className="h-4 w-4 text-muted-foreground" />
              </div>
              <div>
                <p className="font-medium text-sm">Hire &amp; rate</p>
                <p className="text-xs text-muted-foreground">$50 flat fee per hire. Rate after the job.</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Worker welcome
  return (
    <Card className="border-blue-500/30 bg-gradient-to-br from-blue-500/5 to-blue-500/10 relative overflow-hidden">
      <button
        onClick={dismiss}
        className="absolute top-3 right-3 text-muted-foreground hover:text-foreground p-1 z-10"
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" />
      </button>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-xl">
          <HardHat className="h-6 w-6 text-blue-600" />
          Welcome{userName ? `, ${userName}` : ''}! 🎉
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-muted-foreground">
          You&apos;re in. Here&apos;s how to land your first job:
        </p>
        <div className="space-y-3">
          <Link href="/worker/jobs" className="flex items-center gap-3 rounded-xl bg-background border p-3 hover:bg-muted/50 transition-colors active:scale-[0.98]">
            <div className="flex items-center justify-center h-8 w-8 rounded-full bg-blue-500/10 shrink-0">
              <Search className="h-4 w-4 text-blue-600" />
            </div>
            <div>
              <p className="font-medium text-sm">Browse jobs</p>
              <p className="text-xs text-muted-foreground">Swipe right to apply — it&apos;s that easy</p>
            </div>
          </Link>
          <Link href="/worker/profile/edit" className="flex items-center gap-3 rounded-xl bg-background border p-3 hover:bg-muted/50 transition-colors active:scale-[0.98]">
            <div className="flex items-center justify-center h-8 w-8 rounded-full bg-blue-500/10 shrink-0">
              <FileText className="h-4 w-4 text-blue-600" />
            </div>
            <div>
              <p className="font-medium text-sm">Complete your profile</p>
              <p className="text-xs text-muted-foreground">More detail = better matches</p>
            </div>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}
